import React, { useRef, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  FormControl,
  Typography,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import AddIcon from '@material-ui/icons/AddCircleOutlineOutlined'

import { Moment } from 'config/moment'
import { MULTI_SELECT_STRINGS } from 'consts/strings'
import { Dialog, UploadButton, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { PRIZE } from 'consts'
import { StringUtils, ConfigUtils } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { Tag } from 'features/Main/Tags/types'
import { Membership } from 'features/Main/Memberships/types'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import { EditorInput } from 'features/Main/components/EditorInput'
import {
  TimeRestrictionConfig,
  TimeRestrictionConfig as TimeRestrictionConfigType,
} from 'components/TimeRestrictionConfig'

import {
  CreatePrizePayload,
  CreatePrizeResponse,
  Prize,
  MembershipData,
} from '../../../../Prizes/types'
import { createPrize } from '../../../../Prizes/api'
import { useStyles } from './styles'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../constants'
import { PrizeDateRangeFields } from '../PrizeDateRangeFields'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
}

interface TagOption {
  label: string
  value: number
}

// MembershipOption is the same as MembershipData, using MembershipData for consistency
type MembershipOption = MembershipData

const rules = {
  title: [
    {
      validate: (title: string) => StringUtils.extractTextFromHTML(title).length <= 70,
      message: 'Máximo 70 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 40,
      message: 'Máximo 40 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'Máximo 100 caracteres',
    },
  ],
  icon: [],
  expirationDays: [
    {
      validate: (value: string) =>
        ((value && Number(value) >= 1) || !value) && Number.isInteger(Number(value)),
      message: 'El vencimiento debe ser un número entero mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface CreateMembershipPrizeDialogProps {
  handleClose: () => void
  onDone: (prize: Prize) => void
  membershipId: number
  tags: Tag[]
  memberships?: Membership[]
}

const CreatePrizeDialog: React.FC<CreateMembershipPrizeDialogProps> = ({
  handleClose,
  onDone,
  membershipId,
  tags,
  memberships = undefined,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>('')
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>([])
  const [storesEnabled, setStoresEnabled] = useState(true)
  const [tucanEnabled, setTucanEnabled] = useState(false)
  const [toteatEnabled, setToteatEnabled] = useState(false)
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [title, setTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [additionalText, setAdditionalText] = useState('')
  const [rewardType, setrewardType] = useState('')
  const [expirationDays, setExpirationDays] = useState<string>('30')
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [expiration, setExpiration] = useState<Moment | null>(null)

  const handleStartDateChange = (date: Moment | null) => {
    if (date) {
      setStartDate(date.startOf('day'))
    } else {
      setStartDate(null)
    }
  }

  const handleExpirationChange = (date: Moment | null) => {
    if (date) {
      setExpiration(date.endOf('day'))
    } else {
      setExpiration(null)
    }
  }
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(false)
  const allTagsAreRequiredRef = useRef(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [timeRestriction, setTimeRestriction] = useState<TimeRestrictionConfigType>({
    timeRestrictionEnabled: false,
    allowedDays: [],
    startTime: '00:00',
    endTime: '23:59',
  })

  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])
  const [membershipsSelected, setMembershipsSelected] = useState<MembershipOption[]>(() => {
    if (memberships && memberships.length > 0) {
      const initialMembership = memberships.find(m => m.id === membershipId)
      if (initialMembership) {
        return [{ label: initialMembership.title, value: initialMembership.id }]
      }
      return [{ label: memberships[0].title, value: memberships[0].id }]
    }
    return [{ label: `Nivel ${membershipId}`, value: membershipId }]
  })

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))
  const membershipsForSelect = memberships?.map(el => ({ label: el.title, value: el.id })) ?? []

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  const handleSetMemberships = async (e: MembershipOption[]) => {
    setMembershipsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const nextValue = e.target.checked
    allTagsAreRequiredRef.current = nextValue
    setAllTagsAreRequired(nextValue)
  }

  const [createMembershipPrizeApi, isLoading] = useApiCall<CreatePrizePayload, CreatePrizeResponse>(
    createPrize,
  )

  const handleIconChange = (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    setIcon(file)
  }

  const handlerewardTypeSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setrewardType(event.target.value as string)
  }

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'shortTitle',
      'additionalText',
      'icon',
      ...(rewardType === PRIZE.REWARD_TYPE.AUTOMATIC ? ['expirationDays'] : []),
    ]

    if (toteatEnabled) {
      if (toteatCategory === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (toteatCategory === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    shortTitle,
    additionalText,
    icon,
    expirationDays,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    tucanCategory,
    toteatCategory,
  }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors || hasPartnerErrors

  const onCreateMembershipPrize = async () => {
    if (!icon) return
    const prize = {
      title,
      shortTitle,
      icon,
      additionalText,
      rewardType,
      expirationDays,
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      ...(expiration ? { expiration: expiration.toISOString() } : {}),
      tucanCategory,
      toteatCategory,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      tagsSelected,
      allTagsAreRequired: allTagsAreRequiredRef.current,
      timeRestriction,
      membershipsSelected,
    }
    try {
      const { prize: newPrize } = await createMembershipPrizeApi(prize)
      onDone(newPrize)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear el premio. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'
  const tabs = ['Descripción', 'Asignación', 'Horarios']

  const isMembershipRequired = membershipsSelected.length === 0
  const isSubmitDisabledFinal = isSubmitDisabled || isMembershipRequired

  return (
    <Dialog
      title="Nuevo premio"
      isOpen
      showTabsActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabledFinal, startIcon: <AddIcon /> }}
      onAccept={onCreateMembershipPrize}
      style={{
        minWidth: 600,
        minHeight: 640,
        borderRadius: 15,
      }}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
    >
      <TabPanel value={tabIndex} index={0} id="description">
        <Grid container>
          <Grid item xs={12}>
            <FormControl className={classes.fullWidth}>
              <InputLabel id="demo-simple-select-label" className={classes.inputLabel}>
                Tipo de premio
              </InputLabel>
              <Select
                className={classes.inputLabel}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={rewardType}
                onChange={handlerewardTypeSelect}
                fullWidth
              >
                {REWARD_TYPE_BY_MEMBERSHIP.map(value => (
                  <MenuItem className={classes.menuItem} value={value.id} key={value.id}>
                    {value.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <EditorInput
              label="Título"
              value={title}
              readOnly={isLoading}
              required
              onChange={setTitle}
              maxLength={70}
              error={errors.title.hasError}
              errorText={errors.title.message}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              label="Título secundario"
              value={additionalText}
              fullWidth
              onChange={e => setAdditionalText(e.target.value)}
              disabled={isLoading}
              required
              maxLength={100}
              error={errors.additionalText.hasError}
              helperText={
                errors.additionalText.hasError ? errors.additionalText.message : undefined
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              label="Descripción"
              value={shortTitle}
              fullWidth
              onChange={e => setShortTitle(e.target.value)}
              disabled={isLoading}
              required
              maxLength={40}
              error={errors.shortTitle.hasError}
              helperText={errors.shortTitle.hasError ? errors.shortTitle.message : undefined}
            />
          </Grid>

          {rewardType === PRIZE.REWARD_TYPE.AUTOMATIC && (
            <>
              <Grid item xs={12} sm={12}>
                <TextField
                  value={expirationDays}
                  type="number"
                  fullWidth
                  onChange={e => setExpirationDays(e.target.value)}
                  disabled={isLoading}
                  label="Vencimiento"
                  required
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: <InputAdornment position="start">días</InputAdornment>,
                  }}
                  error={errors.expirationDays.hasError}
                  helperText={errors.expirationDays.message}
                />
              </Grid>
            </>
          )}

          <Grid container xs={12} className={classes.iconUploadContainer}>
            <Grid item xs={6} className={classes.iconUploadButton}>
              <UploadButton
                id="manualReward-icon-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Subir ícono premio *"
                onChange={handleIconChange}
                maxSizeInMB={2}
              />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </Grid>
          </Grid>

          <PrizeDateRangeFields
            startDate={startDate}
            expiration={expiration}
            onStartDateChange={handleStartDateChange}
            onExpirationChange={handleExpirationChange}
            disabled={isLoading}
          />

          {hasAnyIntegration && (
            <>
              <Grid item xs={12}>
                <Typography className={classes.dialogSubTitle} style={{ marginTop: 16 }}>
                  Integraciones
                </Typography>
              </Grid>
              <div style={{ marginBottom: 24 }}>
                <PartnerIntegration
                  hideTitle
                  storesEnabled={storesEnabled}
                  setStoresEnabled={setStoresEnabled}
                  tucanEnabled={tucanEnabled}
                  setTucanEnabled={setTucanEnabled}
                  toteatEnabled={toteatEnabled}
                  setToteatEnabled={setToteatEnabled}
                  tucanCategory={tucanCategory}
                  setTucanCategory={setTucanCategory}
                  toteatCategory={toteatCategory}
                  setToteatCategory={setToteatCategory}
                  currentSku={currentSku}
                  setCurrentSku={setCurrentSku}
                  skusList={skusList}
                  setSkusList={setSkusList}
                  toteatBenefitId={toteatBenefitId}
                  setToteatBenefitId={setToteatBenefitId}
                  errors={errors}
                  isLoading={isLoading}
                  onValidationChange={handlePartnerValidationChange}
                />
              </div>
            </>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={1} id="filters">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.dialogSubTitle}>Niveles</Typography>
            <FormControl className={classes.fullWidth}>
              <MultiSelect
                options={membershipsForSelect}
                value={membershipsSelected}
                onChange={handleSetMemberships}
                labelledBy="Seleccionar Niveles"
                hasSelectAll={false}
                className={classes.outlinedMultiSelect}
                overrideStrings={MULTI_SELECT_STRINGS}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.dialogSubTitle}>Tags</Typography>
            <FormControl className={classes.fullWidth}>
              <MultiSelect
                options={tagsForSelect}
                value={tagsSelected}
                onChange={handleSetTags}
                labelledBy="Seleccionar Tags"
                hasSelectAll={false}
                className={classes.outlinedMultiSelect}
                overrideStrings={MULTI_SELECT_STRINGS}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CustomFormControlLabel
              control={
                <CustomCheckbox
                  onChange={handleAllTagsAreRequiredChange}
                  checked={allTagsAreRequired}
                />
              }
              label="Sólo usuarios con todos los tags seleccionados"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={2} id="time-restriction">
        <TimeRestrictionConfig
          config={timeRestriction}
          onChange={setTimeRestriction}
          disabled={isLoading}
          hideTimeSelectors={isEcardEnabled}
        />
      </TabPanel>
    </Dialog>
  )
}

CreatePrizeDialog.defaultProps = {
  memberships: undefined,
}

export { CreatePrizeDialog }
